import { useCallback, useEffect, useState } from "react";

import { useUser } from "MVVM/Hooks/useUser";

const { REACT_APP_STRIPE_ENDPOINT } = process.env;

type TTrialCardStatus = {
  trialing: boolean;
  hasCard: boolean;
  trialEnd: number | null;
  extended: boolean;
};

/**
 * Whether the signed-in trial user has a card attached to their trialing
 * subscription. Answered by the payment endpoint from live Stripe data —
 * default_payment_method isn't on the user record, and a localStorage marker
 * wouldn't survive a second device.
 */
export const useTrialCardStatus = () => {
  const { allstarUser } = useUser();
  const isTrialPeriod = !!allstarUser?.user?.userGroup?.isTrialPeriod;
  const token = allstarUser?.token;

  const [status, setStatus] = useState<TTrialCardStatus | null>(null);
  const [loading, setLoading] = useState(isTrialPeriod);

  const refetch = useCallback(async () => {
    if (!isTrialPeriod || !token) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${REACT_APP_STRIPE_ENDPOINT}/stripe/v1/request?trialCardStatus=1`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (res.ok) {
        setStatus({
          trialing: !!data.trialing,
          hasCard: !!data.hasCard,
          trialEnd: data.trialEnd ?? null,
          extended: !!data.extended,
        });
      }
    } catch {
      // Leave status null — surfaces gated on it simply don't render.
    } finally {
      setLoading(false);
    }
  }, [isTrialPeriod, token]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { status, loading, refetch };
};
